import { ReactNode, RefObject } from 'react';

export interface BaseSelectProps {
  placeholder?: ReactNode;
  searchEnabled?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (searchText: string) => void;
  disableLocalFiltering?: boolean;
  disableSelectAll?: boolean;
  selectedOptions?: {
    id: number | string;
    value: string | number | null;
    label: ReactNode;
    icon?: ReactNode;
  }[];
  variant?: 'text' | 'outlined';
  size?: 'small' | 'medium';
  disabled?: boolean;
  'aria-label': string;
  onDropdownToggle?: (isOpen: boolean) => void;
  /**
   * Overrides when the clear (X) button is shown. Defaults to "whenever
   * something is selected", which is wrong for selects that always hold a
   * value (e.g. sorting) — there the caller passes "is not the default".
   */
  showClear?: boolean;
  listRef?: RefObject<HTMLDivElement | null>;
  /**
   * Minimum width (px) of the options dropdown. By default it matches the
   * select's own width; a narrower select can ask for a wider list.
   */
  listWidth?: number;
  loading?: boolean;
  loadingNextPage?: boolean;
}

export interface SingleSelectInterface<
  T extends string | number | null,
> extends BaseSelectProps {
  multiple?: false;
  value?: T | null;
  onChange: (value: T | null) => void;
  options: {
    id: number | string;
    value: T;
    label: ReactNode;
    text?: string | number;
    icon?: ReactNode;
    disabled?: boolean;
  }[];
}

export interface MultipleSelectInterface<
  T extends string | number | null,
> extends BaseSelectProps {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
  options: {
    id: number | string;
    value: T;
    label: ReactNode;
    text?: string | number;
    icon?: ReactNode;
    disabled?: boolean;
  }[];
}

export type SelectInterface<T extends string | number | null> =
  | SingleSelectInterface<T>
  | MultipleSelectInterface<T>;

function isMultiple<T extends string | number | null>(
  props: SelectInterface<T>,
): props is MultipleSelectInterface<T> {
  return props.multiple === true;
}

export { isMultiple };
